/**
 * @bos/portal3 — QUESTIONS IN CONTEXT: the pure half.
 *
 * THE RULE THIS FILE SERVES (the usability study's §B): *the artifact is the room, and the
 * conversation happens inside it.* A question about the rules used to render at the bottom
 * of a log while the rules themselves sat behind a rail click; the owner was asked to decide
 * something they could not see. Every question already carries the join key — `stage`, on the
 * wire since the first checkpoint document — so the fix is routing, not new server work.
 *
 * Everything here is a PURE function over the payloads the client already holds, so the
 * routing, the summary sentence, the owner/engineer split, the dedupe and the claim match are
 * all testable without a DOM.
 *
 * THE ONE GUARANTEE THAT OUTRANKS THE FEATURE: a question is never stranded somewhere it
 * cannot be answered. A stage with no room — and a question with no stage, or a stage this
 * client has never heard of ('blueprint', 'signoff', anything a newer chain mints) — routes to
 * the OVERVIEW, in full, exactly as it renders today. Routing is a nicety; answerability is
 * the product.
 */
import type { CheckpointQuestion, PortalRunClaim, RunIntakeSurface } from '@bos/portal3-server';
import { ROOMS, roomForStage, type RoomDef } from './model';

// ── 1. routing ───────────────────────────────────────────────────────────────

export interface RoutedQuestions {
  /** Room slug → the questions that belong in it, in the round's own order. */
  readonly byRoom: ReadonlyMap<string, readonly CheckpointQuestion[]>;
  /** The questions no room can hold — they render on the Overview, in full. */
  readonly overview: readonly CheckpointQuestion[];
}

const EMPTY_ROUTED: RoutedQuestions = { byRoom: new Map(), overview: [] };

/** Which room holds a question, by its own `stage` field. Null ⇒ the Overview holds it. */
export function roomForQuestion(q: CheckpointQuestion): RoomDef | null {
  return typeof q.stage === 'string' && q.stage !== '' ? roomForStage(q.stage) : null;
}

/** Split a list of questions (or advisories — same shape) into per-room buckets + the rest. */
export function routeQuestions(items: readonly CheckpointQuestion[]): RoutedQuestions {
  if (items.length === 0) return EMPTY_ROUTED;
  const byRoom = new Map<string, CheckpointQuestion[]>();
  const overview: CheckpointQuestion[] = [];
  for (const q of items) {
    const room = roomForQuestion(q);
    if (room === null) {
      overview.push(q);
      continue;
    }
    const bucket = byRoom.get(room.slug);
    if (bucket === undefined) byRoom.set(room.slug, [q]);
    else bucket.push(q);
  }
  return { byRoom, overview };
}

/** The questions of a round that belong in one room — the room's own slice. */
export function questionsForRoom(
  items: readonly CheckpointQuestion[],
  slug: string,
): readonly CheckpointQuestion[] {
  return routeQuestions(items).byRoom.get(slug) ?? [];
}

// ── 2. the round summary (the Overview's card, the banner's sentence) ────────

export interface RoundFragment {
  /** The room's slug, for the link. Null ⇒ these render right here on the Overview. */
  readonly room: string | null;
  readonly label: string;
  readonly count: number;
}

/**
 * The round, counted per room, in NAV ORDER (never the round's arbitrary order — the summary
 * has to read like the nav beside it). The Overview's own bucket comes last, labelled for what
 * it is: questions that belong to no room.
 */
export function roundFragments(items: readonly CheckpointQuestion[]): readonly RoundFragment[] {
  const routed = routeQuestions(items);
  const out: RoundFragment[] = [];
  for (const room of ROOMS) {
    const n = routed.byRoom.get(room.slug)?.length ?? 0;
    if (n > 0) out.push({ room: room.slug, label: room.label, count: n });
  }
  if (routed.overview.length > 0) {
    out.push({ room: null, label: 'here on the Overview', count: routed.overview.length });
  }
  return out;
}

/** "2 about Rules, 1 about Permissions" — the fragment list as one clause. */
export function fragmentClause(fragments: readonly RoundFragment[]): string {
  return fragments.map((f) => `${f.count} about ${f.label}`).join(', ');
}

/** The count phrase for a round: "3 questions", "1 question". */
export function countPhrase(n: number, noun = 'question'): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * The sticky banner's sentence when the machine stopped to ASK: it names the rooms, because
 * "it has something to ask you" told a person nothing about where to go. Falls back to the
 * plain sentence when the round is not in hand yet — never a guess about rooms.
 */
export function askingBannerSentence(items: readonly CheckpointQuestion[]): string {
  if (items.length === 0) return 'it has something to ask you';
  const fragments = roundFragments(items);
  const clause = fragmentClause(fragments);
  return `${countPhrase(items.length)} ${items.length === 1 ? 'is' : 'are'} waiting for you — ${clause}`;
}

/** Where "Go there" should land: the one room holding the round, or the Overview when it is
 *  spread across several (no room is more the answer than another). */
export function askingBannerRoom(items: readonly CheckpointQuestion[]): string | null {
  const fragments = roundFragments(items);
  return fragments.length === 1 && fragments[0].room !== null ? fragments[0].room : null;
}

// ── 3. M4 — the owner half and the engineer half are two different documents ─

/** The literal marker the gate writes between the two halves of one message. */
export const ENGINEER_MARKER = 'Technical detail (for your engineer):';

export interface SplitMessage {
  /** What the owner is being asked, on its own. Never empty — an unsplittable message is all owner. */
  readonly owner: string;
  /** The machine's own words for whoever writes code, or null when the message carries none. */
  readonly engineer: string | null;
}

/**
 * Split one question/advisory message on the engineer marker.
 *
 * MEASURED (prior review M4, live on the Corner Shop run): all four "Settled for you" advisories
 * welded a good owner sentence to ten lines of `product.record-sale: … could not BIND to a read
 * door` in ONE paragraph. The good sentence drowned. The marker is a literal the gate writes, so
 * this split is mechanical — never a heuristic over prose, and a message without the marker is
 * returned untouched as all-owner text.
 */
export function splitEngineerDetail(message: string): SplitMessage {
  const at = message.indexOf(ENGINEER_MARKER);
  if (at < 0) return { owner: message.trim(), engineer: null };
  const owner = message.slice(0, at).trim();
  const engineer = message.slice(at + ENGINEER_MARKER.length).trim();
  // A message that is ONLY engineer text keeps it as the owner half rather than rendering a card
  // with nothing on it — the person must still see something.
  if (owner === '') return { owner: message.trim(), engineer: null };
  return { owner, engineer: engineer === '' ? null : engineer };
}

// ── 4. M6 — identical rows are one row with a count ─────────────────────────

export interface CountedRow<T> {
  readonly item: T;
  /** How many byte-identical rows this one stands for (1 = it stands only for itself). */
  readonly count: number;
  readonly key: string;
}

/**
 * Fold byte-identical rows together, keeping FIRST-SEEN order.
 *
 * MEASURED (prior review M6, live on the Corner Shop run): 5 of the 6 "Earlier answers kept on
 * record" were word-for-word the same sentence, and the record read like the machine stuttering.
 * The fold is by CONTENT, so nothing distinct is ever merged, and the count is shown — the record
 * still says how many times it happened.
 */
export function countIdentical<T>(items: readonly T[], keyOf: (item: T) => string): readonly CountedRow<T>[] {
  const order: string[] = [];
  const seen = new Map<string, { item: T; count: number }>();
  for (const item of items) {
    const key = keyOf(item);
    const hit = seen.get(key);
    if (hit === undefined) {
      seen.set(key, { item, count: 1 });
      order.push(key);
    } else {
      hit.count += 1;
    }
  }
  return order.map((key) => {
    const row = seen.get(key) as { item: T; count: number };
    return { item: row.item, count: row.count, key };
  });
}

// ── 5. the subject strip's claim matching ────────────────────────────────────

/**
 * WHICH CLAIM KINDS FEED WHICH ROOM (the study's kind→stage map). Used only for the honest
 * mid-derivation fallback: when the artifact a question is about does not exist yet, this says
 * what the machine IS working from. The kinds are the intake's own closed vocabulary
 * (`portal2-server/src/claim-schema.ts`), mirrored here, never imported.
 */
export const CLAIM_KIND_ROOM: Readonly<Record<string, string>> = {
  entity: 'plan/data-model',
  field: 'plan/data-model',
  money: 'plan/data-model',
  identity: 'plan/data-model',
  lifecycle: 'plan/lifecycles',
  workflow: 'plan/operations',
  report: 'plan/operations',
  rule: 'plan/rules',
  constraint: 'plan/rules',
  role: 'plan/permissions',
  integration: 'plan/modules',
};

/** The claims whose kind feeds the room a question is standing in. Empty when nothing does. */
export function claimsFeedingRoom(
  claims: readonly PortalRunClaim[],
  slug: string | null,
): readonly PortalRunClaim[] {
  if (slug === null) return [];
  return claims.filter((c) => CLAIM_KIND_ROOM[c.kind] === slug);
}

/**
 * Normalized for MATCHING ONLY (never for display): case-folded, whitespace-collapsed, and
 * peeled of the wrapping quotes and trailing full stop a sentence-shaped subject carries —
 * `"VAT rate".`, `“VAT rate”` and `VAT rate.` all name one thing, and the peel repeats until
 * nothing more comes off so the order of the punctuation cannot matter.
 */
function normalize(s: string): string {
  let out = s.trim();
  for (;;) {
    const next = out.replace(/^["'“”‘’`\s]+/, '').replace(/["'“”‘’`.\s]+$/, '');
    if (next === out) break;
    out = next;
  }
  return out.replace(/\s+/g, ' ').toLowerCase();
}

/**
 * THE CLAIM BEHIND A QUESTION, or null.
 *
 * DELIBERATELY NARROW, and this is the honesty of it: the match is EXACT-AFTER-NORMALIZATION
 * between the question's per-instance owner fields (`ownerSubject` / `ownerValue`) and the
 * claim's own fields (`title`, `detail`, and the verbatim `literals` the extractor pulled out of
 * the brief). No token overlap, no fuzzy score, no prose parsing. A wrong "From your brief:"
 * quote is worse than none — it would put words in the owner's mouth and invite them to answer a
 * question about something else — so a question this cannot join simply gets no quote.
 */
export function claimForQuestion(
  claims: readonly PortalRunClaim[],
  q: CheckpointQuestion,
): PortalRunClaim | null {
  if (claims.length === 0) return null;
  const needles = [q.ownerSubject, q.ownerValue]
    .filter((s): s is string => typeof s === 'string' && s.trim() !== '')
    .map(normalize)
    .filter((s) => s !== '');
  if (needles.length === 0) return null;
  for (const claim of claims) {
    const hay = [claim.title, claim.detail, ...(claim.literals ?? [])]
      .filter((s): s is string => typeof s === 'string' && s.trim() !== '')
      .map(normalize)
      .filter((s) => s !== '');
    if (hay.some((h) => needles.includes(h))) return claim;
  }
  return null;
}

/** The claim's verbatim brief text: its first recorded literal (the extractor's own excerpt of the
 *  brief), falling back to the claim's detail and then its title. Never a sentence this file wrote. */
export function claimQuote(claim: PortalRunClaim): string {
  const literal = (claim.literals ?? []).find((s) => s.trim() !== '');
  if (literal !== undefined) return literal.trim();
  return claim.detail.trim() !== '' ? claim.detail.trim() : claim.title.trim();
}

/** Does this run carry a grounded brief at all? A pre-intake run carries none, and every
 *  surface that would quote it renders NOTHING rather than an apology. */
export function hasClaims(intake: RunIntakeSurface | undefined): boolean {
  return intake !== undefined && intake.status === 'extracted' && intake.claims.length > 0;
}
